/**
 * Замена `CancelableCompleter` из `package:async` — в вебе его нет.
 *
 * Контроллер чата пользуется ровно четырьмя членами: `isCompleted`,
 * `isCanceled`, `complete()` / `completeError(e)` и `operation.cancel()`.
 * Сам результат никто не слушает: завершение здесь — это только отметка
 * «попытка записи закрыта», по которой `_startRecordAudio` понимает, что
 * предыдущее удержание кнопки уже отработало, а `_startRecorder` — что
 * посетитель отпустил кнопку, пока открывался микрофон.
 *
 * Поэтому настоящего промиса внутри нет: он всё равно остался бы без
 * обработчика, а `completeError` в браузере дал бы `unhandledrejection`.
 * Исход попытки сохраняется в [value] и [error] — прочитать его можно,
 * но никто не обязан.
 */
export class CancelableCompleter<T = void> {
  #completed = false;
  #canceled = false;
  #value: T | undefined = undefined;
  #error: unknown = null;

  /** `completer.isCompleted`. */
  get isCompleted(): boolean {
    return this.#completed;
  }

  /** `completer.isCanceled` — отменённая попытка не считается завершённой. */
  get isCanceled(): boolean {
    return this.#canceled;
  }

  /** Результат успешного завершения; `undefined`, пока его нет. */
  get value(): T | undefined {
    return this.#value;
  }

  /** Ошибка завершения; `null`, пока её нет. */
  get error(): unknown {
    return this.#error;
  }

  /** `completer.complete()`. Повторный вызов и отменённую попытку игнорирует. */
  complete(value?: T): void {
    if (this.#completed || this.#canceled) return;
    this.#completed = true;
    this.#value = value;
  }

  /** `completer.completeError(e)`. */
  completeError(error: unknown): void {
    if (this.#completed || this.#canceled) return;
    this.#completed = true;
    this.#error = error;
  }

  /**
   * `completer.operation` — в Dart это `CancelableOperation`, из которого
   * контроллеру нужен только `cancel()`. Стрелка, а не метод: её передают
   * как есть.
   */
  readonly operation = {
    cancel: (): void => {
      if (this.#completed) return;
      this.#canceled = true;
    },
  };
}
